import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { EMPLOYER_NAV } from "@/pages/employer/EmployerHome";
import { MessageThread } from "@/components/dashboard/MessageThread";
import { SKILLS } from "@/lib/framework";
import { downloadFitPdf } from "@/lib/pdf";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, GripVertical, Users, MessageSquare, MapPin, Sparkles, Wand2, Globe, ImagePlus, X, Pencil, Trash2, Send, Copy, FileText, ChevronDown, TrendingUp, AlertTriangle } from "lucide-react";

const NAME = Object.fromEntries(SKILLS.map((s) => [s.id, s.name]));
const EMPTY = { title: "", job_type: "Job", company: "", location: "", remote_mode: "On-site", pay_min: "", pay_max: "", pay_period: "year", industry: "", company_website: "", company_logo: "", description: "", responsibilities: "", qualifications: "", apply_by: "" };

export default function EmployerJobs() {
  const [jobs, setJobs] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [priority, setPriority] = useState(SKILLS.map((s) => s.id));
  const [saving, setSaving] = useState(false);
  const [applicantsFor, setApplicantsFor] = useState(null);
  const [applicants, setApplicants] = useState(null);
  const [msgApp, setMsgApp] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [aiUrl, setAiUrl] = useState("");
  const [prefilling, setPrefilling] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedApp, setExpandedApp] = useState(null);
  const dragIdx = useRef(null);

  const load = () => api.get("/jobs/mine").then(({ data }) => setJobs(data));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/job-industries").then(({ data }) => setIndustries(data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 256;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const c = document.createElement("canvas");
        c.width = Math.round(img.width * scale);
        c.height = Math.round(img.height * scale);
        c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
        setForm((f) => ({ ...f, company_logo: c.toDataURL("image/png") }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const runPrefill = async () => {
    if (!aiUrl.trim()) { toast.error("Paste a job-listing URL first."); return; }
    setPrefilling(true);
    try {
      const { data } = await api.post("/jobs/ai-prefill", { url: aiUrl.trim() });
      const { skill_priority, ...fields } = data;
      setForm((f) => ({ ...f, ...Object.fromEntries(Object.entries(fields).filter(([k]) => k in EMPTY)) }));
      if (Array.isArray(skill_priority) && skill_priority.length) setPriority(skill_priority);
      toast.success("Fields prefilled from the listing — review and edit before posting.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setPrefilling(false); }
  };

  const onDrop = (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    setPriority((p) => { const n = [...p]; const [m] = n.splice(from, 1); n.splice(i, 0, m); return n; });
    dragIdx.current = null;
  };

  const DEFAULT_PRIORITY = SKILLS.map((s) => s.id);
  const resetForm = () => { setForm(EMPTY); setPriority(DEFAULT_PRIORITY); setEditingId(null); setShowForm(false); };

  const save = async (status) => {
    if (!form.title || !form.company) { toast.error("Title and company are required."); return; }
    setSaving(true);
    try {
      const payload = { ...form, skill_priority: priority, status };
      if (editingId) await api.patch(`/jobs/${editingId}`, payload);
      else await api.post("/jobs", payload);
      toast.success(status === "draft" ? "Draft saved." : editingId ? "Job updated." : "Job posted!");
      resetForm(); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openNew = () => { resetForm(); setShowForm(true); };
  const openEdit = (job) => {
    setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, job[k] ?? EMPTY[k]])) });
    setPriority(job.skill_priority?.length ? job.skill_priority : DEFAULT_PRIORITY);
    setEditingId(job.id); setShowForm(true);
  };
  const publishDraft = async (job) => {
    try { await api.patch(`/jobs/${job.id}`, { ...job, status: "open" }); toast.success("Job published."); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const removeJob = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? This can't be undone.`)) return;
    try { await api.delete(`/jobs/${job.id}`); toast.success("Posting deleted."); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const useLastCompany = () => {
    const src = (jobs || []).find((j) => j.id !== editingId && j.company);
    if (!src) { toast.info("No previous posting to copy from yet."); return; }
    setForm((f) => ({ ...f, company: src.company, company_logo: src.company_logo || "", company_website: src.company_website || "", industry: src.industry || "" }));
    toast.success("Company details copied from your last posting.");
  };

  const openApplicants = async (job) => {
    setApplicantsFor(job); setApplicants(null); setExpandedApp(null);
    const { data } = await api.get(`/jobs/${job.id}/applicants`);
    setApplicants(data.applicants);
  };

  return (
    <DashboardShell title="Job Board" subtitle="Post roles and see applicants ranked by transferable-skill match" nav={EMPLOYER_NAV}>
      <div className="mb-5">
        <Button data-testid="post-job-btn" onClick={openNew} className="rounded-full bg-[#78be21] font-bold text-[#00243f] hover:bg-[#6aac1c]"><Plus className="mr-1 h-4 w-4" /> Post a job / internship</Button>
      </div>

      {!jobs && <div className="flex h-48 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#003057]" /></div>}
      {jobs && jobs.length === 0 && <Card className="p-8 text-center text-slate-500">No postings yet. Click “Post a job” to create your first one.</Card>}
      <div className="space-y-3">
        {jobs && jobs.map((j) => (
          <Card key={j.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid="employer-job-card">
            <div className="flex items-center gap-3">
              {j.company_logo && <img src={j.company_logo} alt={j.company} className="h-11 w-11 flex-shrink-0 rounded-lg border border-slate-200 object-contain bg-white p-1" />}
              <div>
                <span className="text-[11px] font-bold uppercase tracking-widest text-[#5fa114]">{j.job_type} · {j.company}{j.industry ? ` · ${j.industry}` : ""}</span>
                <p className="font-heading text-lg font-bold text-[#003057]">{j.title}</p>
                <p className="flex items-center gap-1.5 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5" /> {j.location || "—"} · {j.remote_mode}</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {j.status === "draft"
                ? <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700" data-testid="job-status-badge">Draft</span>
                : <Button size="sm" variant="outline" className="rounded-full" data-testid="view-applicants" onClick={() => openApplicants(j)}><Users className="mr-1 h-4 w-4" /> {j.applicant_count} applicant{j.applicant_count === 1 ? "" : "s"}</Button>}
              {j.status === "draft" && <Button size="sm" className="rounded-full bg-[#78be21] font-bold text-[#00243f] hover:bg-[#6aac1c]" data-testid="publish-job" onClick={() => publishDraft(j)}><Send className="mr-1 h-4 w-4" /> Publish</Button>}
              <Button size="sm" variant="ghost" className="rounded-full" data-testid="edit-job" onClick={() => openEdit(j)}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" className="rounded-full text-red-500 hover:text-red-600" data-testid="delete-job" onClick={() => removeJob(j)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Post form */}
      <Dialog open={showForm} onOpenChange={(o) => (o ? setShowForm(true) : resetForm())}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-2xl text-[#003057]">{editingId ? "Edit posting" : "Post a job / internship"}</DialogTitle></DialogHeader>

          <div className="mb-2 rounded-xl border border-[#78be21]/45 bg-[#78be21]/8 p-4" data-testid="ai-prefill-box">
            <p className="flex items-center gap-2 text-sm font-bold text-[#003057]"><Wand2 className="h-4 w-4 text-[#5fa114]" /> Prefill with AI</p>
            <p className="mt-0.5 text-xs text-slate-500">Paste a link to an existing job listing and we'll fill the fields in for you to review.</p>
            <div className="mt-2 flex gap-2">
              <Input data-testid="ai-prefill-url" value={aiUrl} onChange={(e) => setAiUrl(e.target.value)} placeholder="https://…/a-job-posting" className="flex-1" />
              <Button type="button" data-testid="ai-prefill-btn" onClick={runPrefill} disabled={prefilling} className="rounded-full bg-[#003057] px-4 font-bold text-white hover:bg-[#004275]">
                {prefilling ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Reading…</> : <><Sparkles className="mr-1 h-4 w-4" /> Prefill</>}
              </Button>
            </div>
          </div>

          <button type="button" onClick={useLastCompany} data-testid="use-last-company" className="mb-1 inline-flex items-center gap-1.5 text-xs font-semibold text-[#005291] hover:text-[#78be21]">
            <Copy className="h-3.5 w-3.5" /> Use my last company details
          </button>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Title *</Label><Input data-testid="job-title" value={form.title} onChange={set("title")} className="mt-1" /></div>
            <div><Label>Type</Label>
              <select data-testid="job-type" value={form.job_type} onChange={set("job_type")} className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
                <option>Job</option><option>Internship</option>
              </select>
            </div>
            <div><Label>Company *</Label><Input data-testid="job-company" value={form.company} onChange={set("company")} className="mt-1" /></div>
            <div><Label>Industry</Label>
              <select data-testid="job-industry" value={form.industry} onChange={set("industry")} className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
                <option value="">Select industry…</option>
                {industries.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2"><Label>Company website</Label>
              <div className="relative mt-1">
                <Globe className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input data-testid="job-website" value={form.company_website} onChange={set("company_website")} placeholder="https://company.com" className="pl-9" />
              </div>
            </div>
            <div><Label>Location</Label><Input data-testid="job-location" value={form.location} onChange={set("location")} placeholder="Houston, TX" className="mt-1" /></div>
            <div><Label>Work mode</Label>
              <select data-testid="job-remote" value={form.remote_mode} onChange={set("remote_mode")} className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
                <option>On-site</option><option>Hybrid</option><option>Remote</option>
              </select>
            </div>
            <div><Label>Pay min</Label><Input data-testid="job-paymin" value={form.pay_min} onChange={set("pay_min")} placeholder="$45,000" className="mt-1" /></div>
            <div><Label>Pay max</Label><Input value={form.pay_max} onChange={set("pay_max")} placeholder="$60,000" className="mt-1" /></div>
            <div><Label>Per</Label>
              <select value={form.pay_period} onChange={set("pay_period")} className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm">
                <option value="year">year</option><option value="hour">hour</option><option value="month">month</option>
              </select>
            </div>
            <div><Label>Apply by</Label><Input type="date" value={form.apply_by} onChange={set("apply_by")} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>Description</Label><Textarea data-testid="job-desc" value={form.description} onChange={set("description")} rows={3} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>Responsibilities</Label><Textarea value={form.responsibilities} onChange={set("responsibilities")} rows={3} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>Qualifications</Label><Textarea value={form.qualifications} onChange={set("qualifications")} rows={3} className="mt-1" /></div>
            <div className="sm:col-span-2"><Label>Company logo</Label>
              <div className="mt-1 flex items-center gap-3">
                {form.company_logo
                  ? <img src={form.company_logo} alt="Logo preview" className="h-14 w-14 rounded-lg border border-slate-200 object-contain bg-white p-1" data-testid="logo-preview" />
                  : <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-dashed border-slate-300 text-slate-300"><ImagePlus className="h-5 w-5" /></div>}
                <label className="cursor-pointer rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-[#003057] transition-colors hover:bg-slate-50">
                  {form.company_logo ? "Change logo" : "Upload logo"}
                  <input type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="logo-upload" />
                </label>
                {form.company_logo && <button type="button" onClick={() => setForm((f) => ({ ...f, company_logo: "" }))} className="text-slate-400 hover:text-red-500" data-testid="logo-remove"><X className="h-4 w-4" /></button>}
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-xl bg-slate-50 p-4">
            <p className="text-sm font-bold text-[#003057]">Rank the transferable skills (most → least important)</p>
            <p className="mb-3 text-xs text-slate-500">Drag to reorder. This is used to match candidates and is not shown on the public listing.</p>
            <div className="space-y-1.5" data-testid="skill-rank">
              {priority.map((id, i) => (
                <div key={id} draggable onDragStart={() => (dragIdx.current = i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                  className="flex cursor-grab items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm active:cursor-grabbing" data-testid={`rank-${id}`}>
                  <span className="font-mono-num w-5 text-xs font-bold text-[#78be21]">{i + 1}</span>
                  <GripVertical className="h-4 w-4 text-slate-300" />
                  <span className="font-semibold text-[#003057]">{NAME[id]}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Button data-testid="job-save-draft" onClick={() => save("draft")} disabled={saving} variant="outline" className="flex-1 rounded-full py-6 font-bold text-[#003057]">
              <FileText className="mr-2 h-4 w-4" /> Save as draft
            </Button>
            <Button data-testid="job-submit" onClick={() => save("open")} disabled={saving} className="flex-1 rounded-full bg-[#78be21] py-6 font-bold text-[#00243f] hover:bg-[#6aac1c]">
              {saving ? "Saving…" : editingId ? "Update & publish" : "Post job"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Applicants */}
      <Dialog open={!!applicantsFor} onOpenChange={(o) => !o && setApplicantsFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading text-xl text-[#003057]">Applicants · {applicantsFor?.title}</DialogTitle></DialogHeader>
          {!applicants && <div className="flex h-32 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#003057]" /></div>}
          {applicants && applicants.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No applicants yet.</p>}
          <div className="space-y-2">
            {applicants && applicants.map((a) => (
              <div key={a.id} className="rounded-xl border border-slate-200 p-3" data-testid="applicant-row">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="font-semibold text-[#003057]">{a.student_name}</p>
                    <p className="text-xs text-slate-500">TSB {a.tsb} · {a.tsb_100}/100</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-full px-2.5 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: a.match >= 80 ? "#10b981" : a.match >= 60 ? "#78be21" : "#d97706" }}>{a.match}%</span>
                    <Button size="sm" variant="outline" className="rounded-full" data-testid="applicant-fit-pdf" onClick={() => downloadFitPdf(applicantsFor, a)}><FileDown className="mr-1 h-4 w-4" /> PDF</Button>
                    <Button size="sm" variant="outline" className="rounded-full" data-testid="message-applicant" onClick={() => setMsgApp(a)}><MessageSquare className="mr-1 h-4 w-4" /> Message</Button>
                  </div>
                </div>
                {a.skill_breakdown && (
                  <div className="mt-2 border-t border-slate-100 pt-2">
                    <div className="flex flex-wrap gap-1.5">
                      {a.skill_breakdown.filter((s) => s.strong).slice(0, 3).map((s) => (
                        <span key={s.id} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><TrendingUp className="h-3 w-3" /> {s.skill}</span>
                      ))}
                      {a.skill_breakdown.filter((s) => s.gap).slice(0, 2).map((s) => (
                        <span key={s.id} className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"><AlertTriangle className="h-3 w-3" /> {s.skill}</span>
                      ))}
                    </div>
                    <button onClick={() => setExpandedApp(expandedApp === a.id ? null : a.id)} data-testid="toggle-skill-fit" className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-[#005291] hover:text-[#78be21]">
                      <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expandedApp === a.id ? "rotate-180" : ""}`} /> Skill fit vs your ranking
                    </button>
                    {expandedApp === a.id && (
                      <div className="mt-2 space-y-1" data-testid="skill-fit-detail">
                        {a.skill_breakdown.map((s) => (
                          <div key={s.id} className="flex items-center gap-2 text-xs">
                            <span className="w-5 font-mono-num font-bold text-slate-400">#{s.rank}</span>
                            <span className="w-36 flex-shrink-0 truncate text-slate-600">{s.skill}</span>
                            <div className="h-2 flex-1 rounded-full bg-slate-100"><div className="h-2 rounded-full" style={{ width: `${s.pct}%`, backgroundColor: s.strong ? "#10b981" : s.gap ? "#d97706" : "#005291" }} /></div>
                            <span className="w-16 text-right font-semibold" style={{ color: s.strong ? "#10b981" : s.gap ? "#d97706" : "#475569" }}>{s.band}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Messaging */}
      <Dialog open={!!msgApp} onOpenChange={(o) => !o && setMsgApp(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-lg text-[#003057]">{msgApp?.student_name}</DialogTitle></DialogHeader>
          {msgApp && <MessageThread applicationId={msgApp.id} />}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
